import type { Money } from "@amber/contracts";
import { budgetValuesSchema, type AllowanceRow, type BudgetsResponse, type BudgetValues } from "../../contracts/index";

export interface Draft {
  infrastructure: string;
  ai: string;
  other: string;
  alert: string;
  suspend: string;
  reserve: string;
  daily: string;
  perJob: string;
  backfill: string;
}

const plain = (m: Money) => String(Math.round(Number(m.amount_decimal) * 100) / 100);

export function draftOf(
  data: Pick<BudgetsResponse, "allowances" | "thresholds" | "reserve_percent" | "limits">,
): Draft {
  const allowance = (key: AllowanceRow["key"]) => {
    const row = data.allowances.find((a) => a.key === key);
    return row ? plain(row.allowance) : "0";
  };
  return {
    infrastructure: allowance("infrastructure"),
    ai: allowance("ai"),
    other: allowance("other"),
    alert: String(data.thresholds.alert_percent),
    suspend: String(data.thresholds.suspend_percent),
    reserve: String(data.reserve_percent),
    daily: plain(data.limits.daily.value),
    perJob: plain(data.limits.per_job.value),
    backfill: String(data.limits.backfill.percent),
  };
}

export function draftFromValues(v: BudgetValues): Draft {
  return {
    infrastructure: v.allowances.infrastructure,
    ai: v.allowances.ai,
    other: v.allowances.other,
    alert: String(v.alert_percent),
    suspend: String(v.suspend_percent),
    reserve: String(v.reserve_percent),
    daily: v.daily_limit,
    perJob: v.per_job_limit,
    backfill: String(v.backfill_percent),
  };
}

export type DraftErrors = Partial<Record<keyof Draft, string>>;

const MONEY = /^\d+(\.\d{1,2})?$/;

export function draftErrors(d: Draft): DraftErrors {
  const out: DraftErrors = {};
  const money = (k: keyof Draft) => {
    if (!MONEY.test(d[k].trim())) out[k] = "Enter an amount such as 150 or 150.50";
  };
  const percent = (k: keyof Draft, min: number, max: number) => {
    const text = d[k].trim();
    const n = Number(text);
    if (!/^\d+$/.test(text) || n < min || n > max)
      out[k] = `Enter a whole number from ${String(min)} to ${String(max)}`;
  };
  money("infrastructure");
  money("ai");
  money("other");
  percent("alert", 1, 99);
  percent("suspend", 1, 99);
  percent("reserve", 0, 50);
  money("daily");
  money("perJob");
  percent("backfill", 0, 100);
  return out;
}

export function valuesOf(d: Draft): BudgetValues | null {
  const parsed = budgetValuesSchema.safeParse({
    allowances: { infrastructure: d.infrastructure.trim(), ai: d.ai.trim(), other: d.other.trim() },
    alert_percent: Number(d.alert.trim()),
    suspend_percent: Number(d.suspend.trim()),
    reserve_percent: Number(d.reserve.trim()),
    daily_limit: d.daily.trim(),
    per_job_limit: d.perJob.trim(),
    backfill_percent: Number(d.backfill.trim()),
  });
  return parsed.success ? parsed.data : null;
}

export interface ComparedValue {
  key: keyof Draft;
  label: string;
  unit: "usd" | "percent";
}

const COMPARE: readonly ComparedValue[] = [
  { key: "infrastructure", label: "Infrastructure allowance", unit: "usd" },
  { key: "ai", label: "AI allowance", unit: "usd" },
  { key: "other", label: "Other allowance", unit: "usd" },
  { key: "alert", label: "Alert threshold", unit: "percent" },
  { key: "suspend", label: "Suspend threshold", unit: "percent" },
  { key: "reserve", label: "Essential-service reserve", unit: "percent" },
  { key: "daily", label: "Daily spend", unit: "usd" },
  { key: "perJob", label: "Per job", unit: "usd" },
  { key: "backfill", label: "Backfill share", unit: "percent" },
];

export function allowanceTotal(d: Draft): number {
  return Number(d.infrastructure) + Number(d.ai) + Number(d.other);
}

export function visibleError(
  k: keyof Draft,
  state: { draft: Draft; initial: Draft; errors: DraftErrors; submitted: boolean },
): string | null {
  return state.submitted || state.draft[k] !== state.initial[k] ? (state.errors[k] ?? null) : null;
}

export function previewKey(values: BudgetValues | null): string {
  return values ? JSON.stringify(values) : "";
}

export function valuesFromKey(key: string): BudgetValues | null {
  const parsed = key ? budgetValuesSchema.safeParse(JSON.parse(key)) : null;
  return parsed?.success ? parsed.data : null;
}

export function differingValues(saved: Draft, draft: Draft): ComparedValue[] {
  return COMPARE.filter((c) => saved[c.key] !== draft[c.key]);
}

export function savedMessage(result: { revision: number; resumed: number }): string {
  return `Budgets saved as revision ${String(result.revision)}${result.resumed > 0 ? ` · ${String(result.resumed)} item${result.resumed === 1 ? "" : "s"} resumed` : ""}`;
}
